import { create } from "zustand";
import menuItems from "../resources/menuItems";
import { getDomains } from "../services/api/domain.service";
import { useHeatmapLayersState } from "./HeatmapLayersState";
import { useTilemapLayersState } from "./TilemapLayersState";
import { useGeoJSONLayersState } from "./GeoJSONLayersState";
import { useParticlesLayersState } from "./ParticlesLayersState";
import { useMarkerLayersState } from "./MarkerLayersState";

const {
  addDomains: addHeatmapDomains,
  removeDomains: removeHeatmapDomains,
  setVariable: setHeatmapVariable,
} = useHeatmapLayersState.getState();

const {
  addDomains: addTilemapDomains,
  removeDomains: removeTilemapDomains,
  setVariable: setTilemapVariable,
} = useTilemapLayersState.getState();

const {
  addDomains: addParticlesDomains,
  removeDomains: removeParticlesDomains,
  setVariable: setParticlesVariable,
} = useParticlesLayersState.getState();

const { addDomains: addGeoJSONDomains, removeDomains: removeGeoJSONDomains } =
  useGeoJSONLayersState.getState();

const { addDomains: addSensorDomains, removeDomains: removeSensorDomains } =
  useMarkerLayersState.getState();

const useMenuState = create((set, get) => ({
  menuItems,
  setMenuItems: (menuItems) => set({ menuItems }),
  setOptionValue: async (optionId, value) => {
    let option;

    set((state) => {
      const updatedMenuItems = state.menuItems.map((section) => ({
        ...section,
        options: updateOption(section.options, optionId, {
          checked: value,
          loading: value,
        }),
      }));
      option = findOptionById(updatedMenuItems, optionId);
      return { menuItems: updatedMenuItems };
    });

    if (option) {
      if (value) {
        await get().activateOption(option);
      } else {
        await get().deactivateOption(option);
      }
    }

    setTimeout(() => {
      set((state) => {
        const updatedMenuItems = state.menuItems.map((section) => ({
          ...section,
          options: updateOption(section.options, optionId, {
            loading: false,
          }),
        }));
        return { menuItems: updatedMenuItems };
      });
    }, 500);
  },

  activateOption: async (option) => {
    // Comprobamos desactivación para cualquier opción que pueda tener un reproductor. En ese caso quitamos
    // todas las otras opciones que, pudiendo tener también reproductor, no tengan la misma variable o no tengan
    // el mismo hourGap. EL PROBLEMA es que a nivel de option no vemos hourGap. Refinar en futuras versiones

    if (
      get().optionHasType(option, "heatmap") ||
      get().optionHasType(option, "tilemap") ||
      get().optionHasType(option, "particles")
    ) {
      const incompatibleOptions = get()
        .getActiveOptions()
        .filter((opt) => {
          // Si ambos son tilemaps (y no es el mismo), se consideran incompatibles
          if (
            get().optionHasType(option, "tilemap") &&
            get().optionHasType(opt, "tilemap") &&
            opt.id !== option.id
          ) {
            return true;
          }

          // Manejo de casos de recortes
          const isSubsetRelation =
            opt.subsetOf === option.id || option.subsetOf === opt.id;

          if (isSubsetRelation) return true;

          // Tilemaps y particles siempre son incompatibles entre sí
          if (
            (get().optionHasType(option, "tilemap") &&
              get().optionHasType(opt, "particles")) ||
            (get().optionHasType(option, "particles") &&
              get().optionHasType(opt, "tilemap"))
          ) {
            return true;
          }

          // Tilemaps y heatmaps siempre son incompatibles entre sí
          if (
            (get().optionHasType(option, "tilemap") &&
              get().optionHasType(opt, "heatmap")) ||
            (get().optionHasType(option, "heatmap") &&
              get().optionHasType(opt, "tilemap"))
          ) {
            return true;
          }

          const typesToCheck = ["heatmap", "tilemap", "particles"];
          const hasType = typesToCheck.some((type) =>
            get().optionHasType(opt, type)
          );
          const differentVariable = opt.variable !== option.variable;
          const differentUnplayable = opt.isUnplayable !== option.isUnplayable;

          if (get().optionHasType(option, "particles")) {
            return hasType && differentUnplayable;
          } else {
            return (
              hasType &&
              ((differentVariable && !get().optionHasType(opt, "particles")) ||
                differentUnplayable)
            );
          }
        });

      for (let otherOpt of incompatibleOptions) {
        console.log("deactivating ", otherOpt.optionName);
        get().setOptionValue(otherOpt.id, false);
      }
    }

    option.controller = new AbortController();

    let {
      heatmapDomains,
      particlesDomains,
      tilemapDomains,
      geoJSONDomains,
      sensorDomains,
    } = await getDomains(option);
    if (heatmapDomains.length) {
      setHeatmapVariable(option.variable);
      await addHeatmapDomains(heatmapDomains);
    }
    if (particlesDomains.length) {
      setParticlesVariable(option.variable);
      await addParticlesDomains(particlesDomains);
    }
    if (tilemapDomains.length) {
      setTilemapVariable(option.variable);
      await addTilemapDomains(tilemapDomains);
    }
    if (geoJSONDomains.length) {
      await addGeoJSONDomains(geoJSONDomains);
    }
    if (sensorDomains.length) {
      await addSensorDomains(sensorDomains);
    }
  },

  deactivateOption: async (option) => {
    if (option.controller) {
      option.controller.abort();
      console.log("Option Aborted: ", option.id);
    }

    removeHeatmapDomains(option.id);
    removeTilemapDomains(option.id);
    removeParticlesDomains(option.id);
    removeGeoJSONDomains(option.id);
    removeSensorDomains(option.id);
  },

  optionHasType: (option, type) => {
    return option.mapResources.some((mr) => mr.resourceType == type);
  },

  getActiveOptions: () => {
    const allOptions = get().menuItems.flatMap((section) =>
      flattenOptions(section.options)
    );
    console.log(allOptions.filter((option) => option.checked));
    return allOptions.filter((option) => option.checked);
  },
  isActive: (optionId) => {
    const allOptions = get().menuItems.flatMap((section) =>
      flattenOptions(section.options)
    );
    return allOptions.some(
      (option) => option.id === optionId && option.checked
    );
  },
  findOptionById: (optionId) => {},
}));

const updateOption = (options, optionId, props) => {
  return options.map((option) => {
    if (option.id === optionId) {
      return { ...option, ...props };
    } else if (option.optionType === "dropdown" && option.options) {
      return {
        ...option,
        options: updateOption(option.options, optionId, props),
      };
    }
    return option;
  });
};

const flattenOptions = (options, flattened = []) => {
  options.forEach((option) => {
    if (option.optionType === "actionable") {
      flattened.push(option);
    } else if (option.optionType === "dropdown" && option.options) {
      flattenOptions(option.options, flattened);
    }
  });
  return flattened;
};

const findOptionById = (menuItems, optionId) => {
  const allOptions = menuItems.flatMap((section) =>
    flattenOptions(section.options)
  );
  return allOptions.find((option) => option.id === optionId);
};

export default useMenuState;
